import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Edit, Trash2, Loader2, PieChart } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AnalyticalAccounting() {
  const [axes, setAxes] = useState([]);
  const [values, setValues] = useState([]);
  const [selectedAxe, setSelectedAxe] = useState('');
  const [loading, setLoading] = useState(true);
  const [axeDialogOpen, setAxeDialogOpen] = useState(false);
  const [valueDialogOpen, setValueDialogOpen] = useState(false);
  const [editingAxe, setEditingAxe] = useState(null);
  const [editingValue, setEditingValue] = useState(null);
  const [axeForm, setAxeForm] = useState({ nom: '', code: '' });
  const [valueForm, setValueForm] = useState({ axe_id: '', code: '', label: '', budget_annuel: '' });

  const fetchAxes = async () => { try { const res = await axios.get(`${API_URL}/analytical-axes`); setAxes(res.data); } catch (e) {} finally { setLoading(false); } };
  const fetchValues = async (axeId) => { try { const res = await axios.get(`${API_URL}/analytical-values`, { params: { axe_id: axeId } }); setValues(res.data); } catch (e) {} };

  useEffect(() => { fetchAxes(); }, []);
  useEffect(() => { if (selectedAxe) fetchValues(selectedAxe); }, [selectedAxe]);

  const saveAxe = async () => {
    if (!axeForm.nom || !axeForm.code) { toast.error('Nom et code requis'); return; }
    try {
      if (editingAxe) { await axios.put(`${API_URL}/analytical-axes/${editingAxe}`, axeForm); toast.success('Axe modifie'); }
      else { await axios.post(`${API_URL}/analytical-axes`, axeForm); toast.success('Axe créé'); }
      setAxeDialogOpen(false); setEditingAxe(null); fetchAxes();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const deleteAxe = async (id) => {
    if (!window.confirm('Supprimer cet axe ?')) return;
    try { await axios.delete(`${API_URL}/analytical-axes/${id}`); toast.success('Axe supprimé'); fetchAxes(); } catch (e) { toast.error('Erreur'); }
  };

  const saveValue = async () => {
    if (!valueForm.code || !valueForm.label || !valueForm.axe_id) { toast.error('Champs requis'); return; }
    try {
      if (editingValue) { await axios.put(`${API_URL}/analytical-values/${editingValue}`, valueForm); toast.success('Valeur modifiée'); }
      else { await axios.post(`${API_URL}/analytical-values`, valueForm); toast.success('Valeur créée'); }
      setValueDialogOpen(false); setEditingValue(null); fetchValues(selectedAxe);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const deleteValue = async (id) => {
    if (!window.confirm('Supprimer ?')) return;
    try { await axios.delete(`${API_URL}/analytical-values/${id}`); toast.success('Supprime'); fetchValues(selectedAxe); } catch (e) { toast.error('Erreur'); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <div><h1 className="text-2xl font-bold"><PieChart className="w-6 h-6 inline mr-2" />Comptabilité analytique</h1></div>

      <Tabs defaultValue="axes">
        <TabsList><TabsTrigger value="axes">Axes analytiques</TabsTrigger><TabsTrigger value="values">Valeurs</TabsTrigger></TabsList>

        <TabsContent value="axes" className="space-y-4">
          <div className="flex justify-end"><Button onClick={() => { setEditingAxe(null); setAxeForm({ nom: '', code: '' }); setAxeDialogOpen(true); }} className="bg-[#0F5156] hover:bg-[#0d464a]"><Plus className="w-4 h-4 mr-2" /> Nouvel axe</Button></div>
          <Card><CardContent className="p-0">
            <Table><TableHeader><TableRow><TableHead>Code</TableHead><TableHead>Nom</TableHead><TableHead>Statut</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
            <TableBody>
              {axes.map(a => (
                <TableRow key={a.id}><TableCell className="font-mono">{a.code}</TableCell><TableCell className="font-medium">{a.nom}</TableCell>
                  <TableCell><Badge variant={a.is_active ? 'default' : 'secondary'}>{a.is_active ? 'Actif' : 'Inactif'}</Badge></TableCell>
                  <TableCell><div className="flex gap-1"><Button variant="ghost" size="sm" onClick={() => { setEditingAxe(a.id); setAxeForm({ nom: a.nom, code: a.code }); setAxeDialogOpen(true); }}><Edit className="w-4 h-4" /></Button><Button variant="ghost" size="sm" onClick={() => deleteAxe(a.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button></div></TableCell>
                </TableRow>
              ))}
              {axes.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-8 text-slate-500">Aucun axe analytique</TableCell></TableRow>}
            </TableBody></Table>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="values" className="space-y-4">
          <div className="flex items-center justify-between gap-4">
            <Select value={selectedAxe} onValueChange={setSelectedAxe}>
              <SelectTrigger className="w-64"><SelectValue placeholder="Choisir un axe" /></SelectTrigger>
              <SelectContent>{axes.map(a => <SelectItem key={a.id} value={a.id}>{a.nom}</SelectItem>)}</SelectContent>
            </Select>
            {selectedAxe && <Button onClick={() => { setEditingValue(null); setValueForm({ axe_id: selectedAxe, code: '', label: '', budget_annuel: '' }); setValueDialogOpen(true); }} className="bg-[#0F5156] hover:bg-[#0d464a]"><Plus className="w-4 h-4 mr-2" /> Nouvelle valeur</Button>}
          </div>
          {selectedAxe && (
            <Card><CardContent className="p-0">
              <Table><TableHeader><TableRow><TableHead>Code</TableHead><TableHead>Label</TableHead><TableHead>Budget annuel</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
              <TableBody>
                {values.map(v => (
                  <TableRow key={v.id}><TableCell className="font-mono">{v.code}</TableCell><TableCell>{v.label}</TableCell>
                    <TableCell>{v.budget_annuel ? formatCurrency(v.budget_annuel) : '-'}</TableCell>
                    <TableCell><div className="flex gap-1"><Button variant="ghost" size="sm" onClick={() => { setEditingValue(v.id); setValueForm({ axe_id: v.axe_id, code: v.code, label: v.label, budget_annuel: v.budget_annuel || '' }); setValueDialogOpen(true); }}><Edit className="w-4 h-4" /></Button><Button variant="ghost" size="sm" onClick={() => deleteValue(v.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button></div></TableCell>
                  </TableRow>
                ))}
                {values.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-8 text-slate-500">Aucune valeur</TableCell></TableRow>}
              </TableBody></Table>
            </CardContent></Card>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={axeDialogOpen} onOpenChange={setAxeDialogOpen}>
        <DialogContent><DialogHeader><DialogTitle>{editingAxe ? 'Modifier l\'axe' : 'Nouvel axe'}</DialogTitle></DialogHeader>
          <div className="space-y-4"><div><Label>Nom *</Label><Input value={axeForm.nom} onChange={e => setAxeForm({ ...axeForm, nom: e.target.value })} placeholder="Ex: Centre de cout" /></div>
            <div><Label>Code *</Label><Input value={axeForm.code} onChange={e => setAxeForm({ ...axeForm, code: e.target.value })} placeholder="Ex: CC" /></div></div>
          <DialogFooter><Button variant="outline" onClick={() => setAxeDialogOpen(false)}>Annuler</Button><Button onClick={saveAxe} className="bg-[#0F5156] hover:bg-[#0d464a]">Enregistrer</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={valueDialogOpen} onOpenChange={setValueDialogOpen}>
        <DialogContent><DialogHeader><DialogTitle>{editingValue ? 'Modifier la valeur' : 'Nouvelle valeur'}</DialogTitle></DialogHeader>
          <div className="space-y-4"><div><Label>Code *</Label><Input value={valueForm.code} onChange={e => setValueForm({ ...valueForm, code: e.target.value })} placeholder="Ex: MKT" /></div>
            <div><Label>Label *</Label><Input value={valueForm.label} onChange={e => setValueForm({ ...valueForm, label: e.target.value })} placeholder="Ex: Marketing" /></div>
            <div><Label>Budget annuel</Label><Input type="number" value={valueForm.budget_annuel} onChange={e => setValueForm({ ...valueForm, budget_annuel: e.target.value })} placeholder="0.00" /></div></div>
          <DialogFooter><Button variant="outline" onClick={() => setValueDialogOpen(false)}>Annuler</Button><Button onClick={saveValue} className="bg-[#0F5156] hover:bg-[#0d464a]">Enregistrer</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
